/**
 * svgAngles — outillage SVG partagé du dossier angles (5e, thème #9).
 *
 * Figures « droites coupées par une (ou deux) sécante(s) » entièrement
 * paramétrées : la géométrie dessinée est EXACTE (la sécante est tracée à
 * ld + m degrés, donc l'angle marqué m° correspond au secteur réel).
 * Précédents repris de gen_661 (6e) : arcD, étiquettes sur bissectrices,
 * salt de variabilité data-v en tête de SVG.
 *
 * Markup balisé pour le solveur géométrique (tests/solvers/angles.js) :
 *   <line class="ln1|ln2|tr1|tr2">  droites / sécantes (coordonnées exactes)
 *   <text class="mv">72°</text>     mesure marquée, posée sur la bissectrice
 *   <text class="sn">①</text>       numéro de secteur (carte 1)
 *   <text class="rl">r</text>       lettre au bout d'une demi-droite
 *   <g class="pt">…<text class="pl">A</text></g>  point nommé (sommet…)
 */

// ─── Emballages payload ──────────────────────────────────────

/** Élément additionnel d'ÉNONCÉ (imageUrl en data-URI : correct ici, §12). */
export const imgElement = (svg, maxWidth = 'small') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
    } }],
});

// ─── Constantes graphiques ───────────────────────────────────

export const BLEU = '#1E3A8A';
export const ROUGE = '#E24B4A';
export const VERT = '#15803d';

/** Chapeau combinant U+0302 : rend la lettre surmontée d'un accent circonflexe. */
export const chapeau = (c) => c + '̂';

/** Nom d'angle xÔy : chapeau sur la lettre du sommet. */
export const nomAngle = (l1, sommet, l2) => `${l1}${chapeau(sommet)}${l2}`;

export const CERCLES = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧'];

// ─── Vecteurs (degrés mathématiques, écran y inversé) ────────

const rad = (d) => (d * Math.PI) / 180;
export const uv = (d) => ({ x: Math.cos(rad(d)), y: -Math.sin(rad(d)) });
export const ptAt = (P, dirDeg, dist) => {
    const u = uv(dirDeg);
    return { x: P.x + dist * u.x, y: P.y + dist * u.y };
};

/** Intersection des droites (P1, d1) et (P2, d2). null si parallèles. */
export const inter = (P1, d1, P2, d2) => {
    const u = uv(d1), v = uv(d2);
    const det = u.x * v.y - u.y * v.x;
    if (Math.abs(det) < 1e-9) return null;
    const t = ((P2.x - P1.x) * v.y - (P2.y - P1.y) * v.x) / det;
    return { x: P1.x + t * u.x, y: P1.y + t * u.y };
};

/** Point de sortie du rayon (P, dirDeg) du cadre réduit de pad. */
export const boutRayon = (P, dirDeg, vb, pad = 12) => {
    const u = uv(dirDeg);
    let t = 1e9;
    if (u.x > 1e-9) t = Math.min(t, (vb.w - pad - P.x) / u.x);
    if (u.x < -1e-9) t = Math.min(t, (pad - P.x) / u.x);
    if (u.y > 1e-9) t = Math.min(t, (vb.h - pad - P.y) / u.y);
    if (u.y < -1e-9) t = Math.min(t, (pad - P.y) / u.y);
    return ptAt(P, dirDeg, Math.max(t, 0));
};

// ─── Secteurs autour d'un sommet (droite dirL × sécante dirT) ─

const norm360 = (d) => ((d % 360) + 360) % 360;
const sinD = (d) => Math.sin(rad(d));

/**
 * Les 4 secteurs au croisement d'une droite (dirL) et d'une sécante (dirT),
 * triés par angle de départ croissant. dirVersAutre = direction du sommet
 * vers l'AUTRE sommet (le long de la sécante) — sert à savoir quels secteurs
 * sont INTÉRIEURS (entre les deux droites).
 *
 * Chaque secteur : { a1, a2, bis, mesure, sideT, sideL, interior }
 *   sideT : côté de la sécante (±1) — comparable entre les deux sommets ;
 *   sideL : côté de SA droite (±1) — comparable si droites quasi parallèles ;
 *   correspondants  : même sideT ET même sideL ;
 *   alternes-internes : sideT opposés ET interior tous deux vrais.
 */
export const secteursAutour = (dirL, dirT, dirVersAutre) => {
    const rays = [norm360(dirL), norm360(dirL + 180), norm360(dirT), norm360(dirT + 180)]
        .sort((a, b) => a - b);
    const refInt = Math.sign(sinD(dirVersAutre - dirL));
    return rays.map((a1, i) => {
        const a2 = i < 3 ? rays[i + 1] : rays[0] + 360;
        const bis = (a1 + a2) / 2;
        return {
            a1, a2, bis: norm360(bis), mesure: Math.round((a2 - a1) * 1000) / 1000,
            sideT: Math.sign(sinD(bis - dirT)),
            sideL: Math.sign(sinD(bis - dirL)),
            interior: Math.sign(sinD(bis - dirL)) === refInt,
        };
    });
};

/** Le secteur (au sommet « autre ») correspondant de s (même sideT, même sideL). */
export const secteurCorrespondant = (s, secteursAutre) =>
    secteursAutre.find((t) => t.sideT === s.sideT && t.sideL === s.sideL);

/** Le secteur alterne-interne de s (côtés de sécante opposés, tous deux intérieurs). */
export const secteurAlterneInterne = (s, secteursAutre) =>
    (s.interior ? secteursAutre.find((t) => t.sideT === -s.sideT && t.interior) : null);

/** Le secteur opposé par le sommet (même sommet, bissectrices à 180°). */
export const secteurOppose = (s, secteursMemeSommet) =>
    secteursMemeSommet.find((t) => Math.abs(norm360(t.bis - s.bis) - 180) < 0.5);

// ─── Constructeur de figure ──────────────────────────────────

/**
 * creerFigure({ w, h, salt }) → objet accumulateur ; .render() ferme le SVG.
 * Le salt data-v est placé AVANT xmlns : la variabilité tombe dans les 80
 * premiers caractères de l'encodage (contrainte fingerprint auditGen).
 */
export const creerFigure = (vb, salt) => {
    const parts = [];
    const fig = {
        vb,
        brut: (s) => { parts.push(s); return fig; },

        /** Droite complète (clippée au cadre) passant par P, direction dirDeg. */
        droite: (P, dirDeg, classe, epaisseur = 1.6) => {
            const A = boutRayon(P, dirDeg + 180, vb, 6);
            const B = boutRayon(P, dirDeg, vb, 6);
            parts.push(`<line class="${classe}" x1="${A.x.toFixed(1)}" y1="${A.y.toFixed(1)}" x2="${B.x.toFixed(1)}" y2="${B.y.toFixed(1)}" stroke="#334155" stroke-width="${epaisseur}"/>`);
            return fig;
        },

        /** Segment simple entre deux points. */
        segment: (P, Q, classe = 'sg', epaisseur = 1.6) => {
            parts.push(`<line class="${classe}" x1="${P.x.toFixed(1)}" y1="${P.y.toFixed(1)}" x2="${Q.x.toFixed(1)}" y2="${Q.y.toFixed(1)}" stroke="#334155" stroke-width="${epaisseur}"/>`);
            return fig;
        },

        /** Lettre (minuscule) au bout du rayon (P, dirDeg), juste dans le cadre. */
        lettreRayon: (P, dirDeg, lettre) => {
            const E = boutRayon(P, dirDeg, vb, 16);
            parts.push(`<text class="rl" x="${E.x.toFixed(1)}" y="${(E.y + 4).toFixed(1)}" font-family="serif" font-style="italic" font-size="14" text-anchor="middle">${lettre}</text>`);
            return fig;
        },

        /** Point nommé : disque + étiquette décalée dans la direction dirLabel. */
        point: (P, label, dirLabelDeg = 90, dist = 13) => {
            const L = ptAt(P, dirLabelDeg, dist);
            parts.push(`<g class="pt"><circle cx="${P.x.toFixed(1)}" cy="${P.y.toFixed(1)}" r="2.4" fill="#0f172a"/><text class="pl" x="${L.x.toFixed(1)}" y="${(L.y + 4).toFixed(1)}" font-family="serif" font-size="14" font-weight="600" text-anchor="middle">${label}</text></g>`);
            return fig;
        },

        /**
         * Marque d'angle : arc dans le secteur [a1°, a2°] (a2 > a1) au sommet V,
         * et texte (mesure « 72° » ou « ? ») posé SUR LA BISSECTRICE — le
         * solveur retrouve le secteur marqué par cette position.
         */
        marque: (V, a1, a2, texte, { couleur = VERT, r = 18, rTexte = 34, classeTexte = 'mv', fs = 12 } = {}) => {
            // Arc dessiné en POLYLIGNE (10 segments de a1 à a2) : robuste, aucun
            // piège de sweep-flag SVG (y écran inversé), toujours du bon côté.
            const n = 10;
            const pts = [];
            for (let i = 0; i <= n; i++) {
                const p = ptAt(V, a1 + ((a2 - a1) * i) / n, r);
                pts.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
            }
            parts.push(`<polyline class="ma" points="${pts.join(' ')}" fill="none" stroke="${couleur}" stroke-width="1.8"/>`);
            if (texte) {
                const T = ptAt(V, (a1 + a2) / 2, rTexte);
                parts.push(`<text class="${classeTexte}" x="${T.x.toFixed(1)}" y="${(T.y + 4).toFixed(1)}" font-family="sans-serif" font-size="${fs}" fill="${couleur}" text-anchor="middle">${texte}</text>`);
            }
            return fig;
        },

        /** Codage d'angle droit : petit carré entre les rayons a1 et a2 (≈ 90°). */
        codageDroit: (V, a1, a2, { couleur = VERT, c = 11 } = {}) => {
            const p1 = ptAt(V, a1, c);
            const p3 = ptAt(V, a2, c);
            const p2 = { x: p1.x + p3.x - V.x, y: p1.y + p3.y - V.y };
            parts.push(`<path class="cd" d="M ${p1.x.toFixed(1)},${p1.y.toFixed(1)} L ${p2.x.toFixed(1)},${p2.y.toFixed(1)} L ${p3.x.toFixed(1)},${p3.y.toFixed(1)}" fill="none" stroke="${couleur}" stroke-width="1.6"/>`);
            return fig;
        },

        /** Numéro de secteur (①…⑧) posé sur la bissectrice bis au sommet V. */
        numeroSecteur: (V, bis, numero, r = 30) => {
            const T = ptAt(V, bis, r);
            parts.push(`<text class="sn" x="${T.x.toFixed(1)}" y="${(T.y + 5).toFixed(1)}" font-family="sans-serif" font-size="15" fill="${BLEU}" text-anchor="middle">${numero}</text>`);
            return fig;
        },

        /** Codage de longueurs égales : petit trait perpendiculaire au milieu de [PQ]. */
        codageLongueur: (P, Q, { couleur = '#0f172a', demi = 5 } = {}) => {
            const M = { x: (P.x + Q.x) / 2, y: (P.y + Q.y) / 2 };
            const d = Math.atan2(-(Q.y - P.y), Q.x - P.x) * 180 / Math.PI + 90;
            const a = ptAt(M, d, demi);
            const b = ptAt(M, d + 180, demi);
            parts.push(`<line class="tk" x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${b.x.toFixed(1)}" y2="${b.y.toFixed(1)}" stroke="${couleur}" stroke-width="1.8"/>`);
            return fig;
        },

        render: () =>
            `<svg data-v="${salt}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}">${parts.join('')}</svg>`,
    };
    return fig;
};
